import React, { useEffect, useState, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Select,
  Space,
  Tag,
  Modal,
  Form,
  Popconfirm,
  message,
  InputNumber,
  Row,
  Col,
  Typography,
  Divider,
  Statistic,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  DollarOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { knowledgeApi } from '../../services/knowledge';
import type { PricingBenchmark, CreatePricingBenchmarkRequest } from '../../types/knowledge';
import type { PaginationParams } from '../../types/api';

const { Option } = Select;
const { Text, Title } = Typography;

const PLATFORMS = [
  { value: 'youtube', label: 'YouTube' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'instagram', label: 'Instagram' },
];
const FOLLOWER_TIERS = [
  { value: 'nano', label: '纳米级 (<1万)' },
  { value: 'micro', label: '微网红 (1-10万)' },
  { value: 'mid', label: '中等级 (10-50万)' },
  { value: 'macro', label: '大网红 (50-100万)' },
  { value: 'mega', label: '顶级网红 (>100万)' },
];
const CONTENT_TYPES = [
  { value: 'shorts', label: '短视频' },
  { value: 'video', label: '视频' },
  { value: '贴片', label: '贴片广告' },
  { value: '直播', label: '直播' },
];
const INDUSTRIES = ['科技', '美妆', '时尚', '游戏', '美食', '健身', '教育', '金融'];
const COUNTRIES = ['中国', '美国', '英国', '德国', '法国', '日本', '韩国'];

const PLATFORM_COLOR_MAP: Record<string, string> = {
  youtube: 'red',
  tiktok: 'cyan',
  instagram: 'magenta',
};

const TIER_COLOR_MAP: Record<string, string> = {
  nano: 'default',
  micro: 'green',
  mid: 'blue',
  macro: 'orange',
  mega: 'red',
};

export const PricingBenchmarks: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<PricingBenchmark[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [filterPlatform, setFilterPlatform] = useState<string>('');
  const [filterTier, setFilterTier] = useState<string>('');
  const [modalVisible, setModalVisible] = useState(false);
  const [queryModalVisible, setQueryModalVisible] = useState(false);
  const [editingBenchmark, setEditingBenchmark] = useState<PricingBenchmark | null>(null);
  const [queryResult, setQueryResult] = useState<PricingBenchmark | null>(null);
  const [queryLoading, setQueryLoading] = useState(false);
  const [queryForm] = Form.useForm();
  const [refreshKey, setRefreshKey] = useState(0);
  const [form] = Form.useForm();

  const fetchData = useCallback(async (params?: PaginationParams) => {
    setLoading(true);
    try {
      const response = await knowledgeApi.getPricingBenchmarks({
        page: params?.page || pagination.current,
        page_size: params?.page_size || pagination.pageSize,
      });
      // 后端返回格式: { data: T[], pagination: { total, page, page_size, total_pages } }
      setData(response.data || []);
      if (response.pagination) {
        setPagination({
          current: response.pagination.page,
          pageSize: response.pagination.page_size,
          total: response.pagination.total,
        });
      }
    } catch {
      // 错误已在request拦截器中处理
    } finally {
      setLoading(false);
    }
  }, [pagination.current, pagination.pageSize]);

  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const handleTableChange = (newPagination: { current?: number; pageSize?: number }) => {
    fetchData({
      page: newPagination.current,
      page_size: newPagination.pageSize,
    });
  };

  const handleCreate = () => {
    setEditingBenchmark(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record: PricingBenchmark) => {
    setEditingBenchmark(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await knowledgeApi.deletePricingBenchmark(id);
      message.success('删除成功');
      setRefreshKey(prev => prev + 1);
    } catch (error: unknown) {
      console.error('[PricingBenchmarks] 操作失败:', error);
      message.error('操作失败，请检查权限或稍后重试');
    }
  };

  const handleSubmit = async (values: CreatePricingBenchmarkRequest) => {
    try {
      if (editingBenchmark) {
        await knowledgeApi.updatePricingBenchmark(editingBenchmark.id, values);
        message.success('更新成功');
      } else {
        await knowledgeApi.createPricingBenchmark(values);
        message.success('创建成功');
      }
      setModalVisible(false);
      form.resetFields();
      setRefreshKey(prev => prev + 1);
    } catch (error: unknown) {
      console.error('[PricingBenchmarks] 操作失败:', error);
      message.error('操作失败，请检查权限或稍后重试');
    }
  };

  const handleQueryPrice = async (values: { platform: string; follower_tier: string; content_type: string }) => {
    setQueryLoading(true);
    try {
      const response = await knowledgeApi.queryPricingBenchmarks(values);
      if (response.data && response.data.length > 0) {
        setQueryResult(response.data[0]);
      } else {
        setQueryResult(null);
        message.info('未找到匹配的报价数据');
      }
    } catch {
      // 错误已处理
    } finally {
      setQueryLoading(false);
    }
  };

  const filteredData = data.filter((item) => {
    const matchPlatform = !filterPlatform || item.platform === filterPlatform;
    const matchTier = !filterTier || item.follower_tier === filterTier;
    return matchPlatform && matchTier;
  });

  const columns: ColumnsType<PricingBenchmark> = [
    {
      title: '平台',
      dataIndex: 'platform',
      key: 'platform',
      width: 100,
      render: (platform: string) => (
        <Tag color={PLATFORM_COLOR_MAP[platform] || 'default'}>{platform}</Tag>
      ),
    },
    {
      title: '红人等级',
      dataIndex: 'follower_tier',
      key: 'follower_tier',
      width: 120,
      render: (tier: string) => {
        const tierInfo = FOLLOWER_TIERS.find((t) => t.value === tier);
        return (
          <Tag color={TIER_COLOR_MAP[tier] || 'default'}>
            {tierInfo?.label || tier}
          </Tag>
        );
      },
    },
    {
      title: '内容类型',
      dataIndex: 'content_type',
      key: 'content_type',
      width: 100,
      render: (type: string) => <Tag>{type}</Tag>,
    },
    {
      title: '行业',
      dataIndex: 'industry',
      key: 'industry',
      width: 80,
    },
    {
      title: '国家',
      dataIndex: 'country',
      key: 'country',
      width: 80,
    },
    {
      title: '平均CPM',
      dataIndex: 'avg_cpm',
      key: 'avg_cpm',
      width: 100,
      render: (cpm: number) => <Text strong>¥{cpm?.toFixed(2) || '-'}</Text>,
    },
    {
      title: '价格区间',
      key: 'price_range',
      width: 180,
      render: (_: unknown, record: PricingBenchmark) => (
        <Text>
          ¥{record.price_range_low?.toLocaleString()} - ¥{record.price_range_high?.toLocaleString()}
        </Text>
      ),
    },
    {
      title: '样本数',
      dataIndex: 'sample_count',
      key: 'sample_count',
      width: 80,
      render: (count: number) => count || 0,
    },
    {
      title: '更新时间',
      dataIndex: 'last_updated',
      key: 'last_updated',
      width: 120,
      render: (text: string) => text?.split('T')[0] || '-',
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      fixed: 'right' as const,
      render: (_: unknown, record: PricingBenchmark) => (
        <Space size="small">
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定删除此报价基准？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="text" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* 搜索和操作栏 */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <Space size="middle">
              <Select
                placeholder="平台筛选"
                value={filterPlatform || undefined}
                onChange={setFilterPlatform}
                style={{ width: 120 }}
                allowClear
              >
                {PLATFORMS.map((p) => (
                  <Option key={p.value} value={p.value}>{p.label}</Option>
                ))}
              </Select>
              <Select
                placeholder="红人等级"
                value={filterTier || undefined}
                onChange={setFilterTier}
                style={{ width: 150 }}
                allowClear
              >
                {FOLLOWER_TIERS.map((t) => (
                  <Option key={t.value} value={t.value}>{t.label}</Option>
                ))}
              </Select>
              <Button
                icon={<DollarOutlined />}
                onClick={() => setQueryModalVisible(true)}
              >
                查询报价
              </Button>
            </Space>
          </Col>
          <Col>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
              新建报价
            </Button>
          </Col>
        </Row>
      </Card>

      {/* 数据表格 */}
      <Table
        columns={columns}
        dataSource={filteredData}
        rowKey="id"
        loading={loading}
        scroll={{ x: 1300 }}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showTotal: (total) => `共 ${total} 条`,
        }}
        onChange={handleTableChange}
      />

      {/* 新建/编辑弹窗 */}
      <Modal
        title={editingBenchmark ? '编辑报价基准' : '新建报价基准'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        width={700}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="platform"
                label="平台"
                rules={[{ required: true, message: '请选择平台' }]}
              >
                <Select placeholder="选择平台">
                  {PLATFORMS.map((p) => (
                    <Option key={p.value} value={p.value}>{p.label}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="follower_tier"
                label="红人等级"
                rules={[{ required: true, message: '请选择红人等级' }]}
              >
                <Select placeholder="选择红人等级">
                  {FOLLOWER_TIERS.map((t) => (
                    <Option key={t.value} value={t.value}>{t.label}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="content_type"
                label="内容类型"
                rules={[{ required: true, message: '请选择内容类型' }]}
              >
                <Select placeholder="选择内容类型">
                  {CONTENT_TYPES.map((t) => (
                    <Option key={t.value} value={t.value}>{t.label}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="industry"
                label="行业"
                rules={[{ required: true, message: '请选择行业' }]}
              >
                <Select placeholder="选择行业">
                  {INDUSTRIES.map((i) => (
                    <Option key={i} value={i}>{i}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="country"
                label="国家"
                rules={[{ required: true, message: '请选择国家' }]}
              >
                <Select placeholder="选择国家">
                  {COUNTRIES.map((c) => (
                    <Option key={c} value={c}>{c}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="avg_cpm"
                label="平均CPM（元）"
                rules={[
                  { required: true, message: '请输入平均CPM' },
                  { type: 'number', min: 0, message: 'CPM不能为负数' },
                ]}
              >
                <InputNumber min={0} precision={2} style={{ width: '100%' }} placeholder="0.00" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="price_range_low"
                label="最低价格（元）"
                rules={[
                  { required: true, message: '请输入最低价格' },
                  { type: 'number', min: 0, message: '价格不能为负数' },
                  {
                    validator: (_, value) => {
                      const high = form.getFieldValue('price_range_high');
                      if (value && high && value > high) {
                        return Promise.reject('最低价格不能超过最高价格');
                      }
                      return Promise.resolve();
                    }
                  }
                ]}
              >
                <InputNumber min={0} style={{ width: '100%' }} placeholder="0" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="price_range_high"
                label="最高价格（元）"
                rules={[
                  { required: true, message: '请输入最高价格' },
                  { type: 'number', min: 0, message: '价格不能为负数' },
                ]}
              >
                <InputNumber min={0} style={{ width: '100%' }} placeholder="0" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* 查询报价弹窗 */}
      <Modal
        title="查询参考报价"
        open={queryModalVisible}
        onCancel={() => {
          setQueryModalVisible(false);
          setQueryResult(null);
          queryForm.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Form form={queryForm} layout="vertical" onFinish={handleQueryPrice}>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="platform"
                label="平台"
                rules={[{ required: true, message: '请选择平台' }]}
              >
                <Select placeholder="选择平台">
                  {PLATFORMS.map((p) => (
                    <Option key={p.value} value={p.value}>{p.label}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="follower_tier"
                label="红人等级"
                rules={[{ required: true, message: '请选择红人等级' }]}
              >
                <Select placeholder="选择红人等级">
                  {FOLLOWER_TIERS.map((t) => (
                    <Option key={t.value} value={t.value}>{t.label}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="content_type"
                label="内容类型"
                rules={[{ required: true, message: '请选择内容类型' }]}
              >
                <Select placeholder="选择内容类型">
                  {CONTENT_TYPES.map((t) => (
                    <Option key={t.value} value={t.value}>{t.label}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              icon={<SearchOutlined />}
              loading={queryLoading}
              block
            >
              查询报价
            </Button>
          </Form.Item>
        </Form>

        {queryResult && (
          <>
            <Divider>查询结果</Divider>
            <Card>
              <Row gutter={24}>
                <Col span={12}>
                  <Statistic
                    title="平均CPM"
                    value={queryResult.avg_cpm}
                    precision={2}
                    prefix="¥"
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title="价格区间"
                    value={`¥${(queryResult.price_range_low || 0).toLocaleString()} - ¥${(queryResult.price_range_high || 0).toLocaleString()}`}
                  />
                </Col>
              </Row>
              <Row gutter={24} style={{ marginTop: 16 }}>
                <Col span={12}>
                  <Text type="secondary">平台：{queryResult.platform}</Text>
                </Col>
                <Col span={12}>
                  <Text type="secondary">行业：{queryResult.industry}</Text>
                </Col>
              </Row>
              <Row gutter={24} style={{ marginTop: 8 }}>
                <Col span={12}>
                  <Text type="secondary">样本数：{queryResult.sample_count}</Text>
                </Col>
                <Col span={12}>
                  <Text type="secondary">更新时间：{queryResult.last_updated?.split('T')[0]}</Text>
                </Col>
              </Row>
            </Card>
          </>
        )}
      </Modal>
    </div>
  );
};
